import apiConfig from 'app/api/apiConfig.json';
import { getLocalApi } from './localApi';

export interface BookingSystemProductExclusion {
  productId: number;
  productTypeId: number;
  productTypeName?: string;
}

export interface BookingSystemDivisionExclusion {
  divisionId: number | string;
  divisionName?: string;
}

export interface BookingSystemSchoolExclusion {
  schoolId: number | string;
  schoolName?: string;
}

export interface BookingSystemExclusionSummary {
  bookingSystemId: number;
  bookingSystemName: string;
  productsExcluded: BookingSystemProductExclusion[];
  divisionsExcluded: BookingSystemDivisionExclusion[];
  schoolsExcluded: BookingSystemSchoolExclusion[];
}

interface ExclusionMutationResult {
  ok: boolean;
  message?: string;
}

const buildMutationUrl = (
  bookingSystemId: number | string,
  productTypeId: number | string,
  productId: number | string
) =>
  apiConfig.booking_system_exclusion_update
    .replace('{bookingSystemId}', String(bookingSystemId))
    .replace('{productTypeId}', String(productTypeId))
    .replace('{productId}', String(productId));

const buildDivisionMutationUrl = (
  bookingSystemId: number | string,
  divisionId: number | string
) =>
  apiConfig.booking_system_exclusion_update_division
    .replace('{bookingSystemId}', String(bookingSystemId))
    .replace('{divisionId}', String(divisionId));

const buildSchoolMutationUrl = (
  bookingSystemId: number | string,
  schoolId: number | string
) =>
  apiConfig.booking_system_exclusion_update_school
    .replace('{bookingSystemId}', String(bookingSystemId))
    .replace('{schoolId}', String(schoolId));

const mapProductExclusion = (dto: any): BookingSystemProductExclusion => ({
  productId: dto?.productId,
  productTypeId: dto?.productTypeId,
  productTypeName: dto?.productTypeName,
});

const mapDivisionExclusion = (dto: any): BookingSystemDivisionExclusion => ({
  divisionId: dto?.divisionId ?? dto?.id ?? dto,
  divisionName: dto?.divisionName ?? dto?.name,
});

const mapSchoolExclusion = (dto: any): BookingSystemSchoolExclusion => ({
  schoolId: dto?.schoolId ?? dto?.id ?? dto,
  schoolName: dto?.schoolName ?? dto?.name,
});

const mapSummary = (dto: any): BookingSystemExclusionSummary => ({
  bookingSystemId: dto?.bookingSystemId,
  bookingSystemName: dto?.bookingSystemName,
  productsExcluded: Array.isArray(dto?.productsExcluded)
    ? dto.productsExcluded.map(mapProductExclusion)
    : [],
  divisionsExcluded: Array.isArray(dto?.divisionsExcluded ?? dto?.divisionExcluded)
    ? (dto?.divisionsExcluded ?? dto?.divisionExcluded).map(mapDivisionExclusion)
    : [],
  schoolsExcluded: Array.isArray(dto?.schoolsExcluded ?? dto?.schoolExcluded)
    ? (dto?.schoolsExcluded ?? dto?.schoolExcluded).map(mapSchoolExclusion)
    : [],
});

const parseMessage = async (response: Response | undefined) => {
  if (!response) {
    return undefined;
  }
  try {
    const data = await response.clone().json();
    if (typeof data === 'string') {
      return data;
    }
    if (data && typeof data.message === 'string') {
      return data.message;
    }
  } catch {
    // fall through to attempt reading as plain text
  }
  try {
    return await response.text();
  } catch {
    return undefined;
  }
};

export async function getBookingSystemProductExclusions(): Promise<BookingSystemExclusionSummary[]> {
  const localApi = await getLocalApi();
  return localApi.fetchList(
    apiConfig.booking_system_exclusion_get_products,
    mapSummary,
    'Failed to fetch booking system exclusions'
  );
}

export async function addBookingSystemProductExclusion(
  bookingSystemId: number | string,
  productTypeId: number | string,
  productId: number | string
): Promise<ExclusionMutationResult> {
  const localApi = await getLocalApi();
  const response = await localApi.call(
    buildMutationUrl(bookingSystemId, productTypeId, productId),
    'POST',
    JSON.stringify({
      bookingSystemId,
      productTypeId,
      productId,
    })
  );
  return {
    ok: !!response?.ok,
    message: await parseMessage(response),
  };
}

export async function removeBookingSystemProductExclusion(
  bookingSystemId: number | string,
  productTypeId: number | string,
  productId: number | string
): Promise<ExclusionMutationResult> {
  const localApi = await getLocalApi();
  const response = await localApi.call(
    buildMutationUrl(bookingSystemId, productTypeId, productId),
    'DELETE',
    undefined
  );
  return {
    ok: !!response?.ok,
    message: await parseMessage(response),
  };
}

const normalizeIds = (ids: (number | string)[] | undefined): string[] =>
  Array.isArray(ids) ? ids.map(id => String(id)) : [];

const parseBookingSystemId = (id: number | string | null | undefined): number | null => {
  if (id === null || id === undefined) return null;
  const num = Number(id);
  if (!Number.isNaN(num) && num > 0) return num;
  const str = String(id).trim();
  const match = /^BookingSystem-(\d+)$/i.exec(str);
  if (match) return parseInt(match[1], 10);
  const numFromStr = parseInt(str, 10);
  return Number.isNaN(numFromStr) || numFromStr <= 0 ? null : numFromStr;
};

const normalizeSchoolIdForComparison = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const match = /^(?:School-?|CN)?(\d+)$/i.exec(trimmed);
  return match ? match[1]! : trimmed;
};

export async function getExcludedBookingSystemIdsForProduct(
  productId: number | string,
  productTypeId: number | string
): Promise<string[]> {
  const exclusions = await getBookingSystemProductExclusions();
  const normalizedProductId = Number(productId);
  const normalizedProductTypeId = Number(productTypeId);

  return exclusions
    .filter(summary => {
      if (!Array.isArray(summary.productsExcluded)) {
        return false;
      }
      return summary.productsExcluded.some(product =>
        Number(product.productId) === normalizedProductId &&
        Number(product.productTypeId) === normalizedProductTypeId
      );
    })
    .map(summary => String(summary.bookingSystemId));
}

export async function updateBookingSystemProductExclusions(
  productId: number | string,
  productTypeId: number | string,
  selectedIds: (number | string)[] | undefined,
  previousIds: (number | string)[] | undefined
): Promise<string[]> {
  const normalizedSelected = normalizeIds(selectedIds);
  const normalizedPrevious = normalizeIds(previousIds);

  const additions = normalizedSelected.filter(id => !normalizedPrevious.includes(id));
  const removals = normalizedPrevious.filter(id => !normalizedSelected.includes(id));

  const numericProductId = Number(productId);
  const effectiveProductId = Number.isNaN(numericProductId) ? productId : numericProductId;

  for (const id of additions) {
    const maybeNumeric = Number(id);
    const bookingSystemId = Number.isNaN(maybeNumeric) ? id : maybeNumeric;
    const result = await addBookingSystemProductExclusion(
      bookingSystemId,
      productTypeId,
      effectiveProductId
    );
    if (!result.ok) {
      throw new Error(result.message ?? `Failed to exclude product for booking system ${id}.`);
    }
  }

  for (const id of removals) {
    const maybeNumeric = Number(id);
    const bookingSystemId = Number.isNaN(maybeNumeric) ? id : maybeNumeric;
    const result = await removeBookingSystemProductExclusion(
      bookingSystemId,
      productTypeId,
      effectiveProductId
    );
    if (!result.ok) {
      throw new Error(result.message ?? `Failed to include product for booking system ${id}.`);
    }
  }

  return normalizedSelected;
}

export async function addBookingSystemDivisionExclusion(
  bookingSystemId: number | string,
  divisionId: number | string
): Promise<ExclusionMutationResult> {
  const localApi = await getLocalApi();
  const numericDivisionId = Number(divisionId);
  const numericBookingSystemId = Number(bookingSystemId);
  const payload = {
    divisionId: Number.isNaN(numericDivisionId) ? divisionId : numericDivisionId,
    bookingSystemId: Number.isNaN(numericBookingSystemId) ? bookingSystemId : numericBookingSystemId,
  };
  const response = await localApi.call(
    buildDivisionMutationUrl(bookingSystemId, divisionId),
    'POST',
    JSON.stringify(payload)
  );
  return {
    ok: !!response?.ok,
    message: await parseMessage(response),
  };
}

export async function removeBookingSystemDivisionExclusion(
  bookingSystemId: number | string,
  divisionId: number | string
): Promise<ExclusionMutationResult> {
  const localApi = await getLocalApi();
  const response = await localApi.call(
    buildDivisionMutationUrl(bookingSystemId, divisionId),
    'DELETE',
    undefined
  );
  return {
    ok: !!response?.ok,
    message: await parseMessage(response),
  };
}

export async function getExcludedBookingSystemIdsForDivision(
  divisionId: number | string
): Promise<string[]> {
  const localApi = await getLocalApi();
  const summaries = await localApi.fetchList(
    apiConfig.booking_system_exclusion_get_divisions,
    mapSummary,
    'Failed to fetch division exclusions'
  );
  const normalizedDivisionId = String(divisionId);
  const result: string[] = [];

  summaries.forEach(summary => {
    const hasDivision = summary.divisionsExcluded.some(division =>
      String(division.divisionId) === normalizedDivisionId
    );
    if (hasDivision) {
      result.push(String(summary.bookingSystemId));
    }
  });

  return result;
}

export async function updateBookingSystemDivisionExclusions(
  divisionId: number | string,
  selectedIds: (number | string)[] | undefined,
  previousIds: (number | string)[] | undefined
): Promise<string[]> {
  const normalizedSelected = normalizeIds(selectedIds);
  const normalizedPrevious = normalizeIds(previousIds);

  const additions = normalizedSelected.filter(id => !normalizedPrevious.includes(id));
  const removals = normalizedPrevious.filter(id => !normalizedSelected.includes(id));

  const numericDivisionId = Number(divisionId);
  const effectiveDivisionId = Number.isNaN(numericDivisionId) ? divisionId : numericDivisionId;

  for (const systemId of additions) {
    const numeric = Number(systemId);
    const bookingSystemId = Number.isNaN(numeric) ? systemId : numeric;
    const result = await addBookingSystemDivisionExclusion(
      bookingSystemId,
      effectiveDivisionId
    );
    if (!result.ok) {
      throw new Error(result.message ?? `Failed to exclude division for booking system ${systemId}.`);
    }
  }

  for (const systemId of removals) {
    const numeric = Number(systemId);
    const bookingSystemId = Number.isNaN(numeric) ? systemId : numeric;
    const result = await removeBookingSystemDivisionExclusion(
      bookingSystemId,
      effectiveDivisionId
    );
    if (!result.ok) {
      throw new Error(result.message ?? `Failed to include division for booking system ${systemId}.`);
    }
  }

  return normalizedSelected;
}

export async function addBookingSystemSchoolExclusion(
  bookingSystemId: number | string,
  schoolId: number | string
): Promise<ExclusionMutationResult> {
  const localApi = await getLocalApi();
  const numericSchoolId = Number(schoolId);
  const numericBookingSystemId = Number(bookingSystemId);
  const payload = {
    schoolId: Number.isNaN(numericSchoolId) ? schoolId : numericSchoolId,
    bookingSystemId: Number.isNaN(numericBookingSystemId) ? bookingSystemId : numericBookingSystemId,
  };
  const response = await localApi.call(
    buildSchoolMutationUrl(bookingSystemId, schoolId),
    'POST',
    JSON.stringify(payload)
  );
  return {
    ok: !!response?.ok,
    message: await parseMessage(response),
  };
}

export async function removeBookingSystemSchoolExclusion(
  bookingSystemId: number | string,
  schoolId: number | string
): Promise<ExclusionMutationResult> {
  const localApi = await getLocalApi();
  const response = await localApi.call(
    buildSchoolMutationUrl(bookingSystemId, schoolId),
    'DELETE',
    undefined
  );
  return {
    ok: !!response?.ok,
    message: await parseMessage(response),
  };
}

export async function getExcludedBookingSystemIdsForSchool(
  schoolId: number | string
): Promise<string[]> {
  const localApi = await getLocalApi();
  const summaries = await localApi.fetchList(
    apiConfig.booking_system_exclusion_get_schools,
    mapSummary,
    'Failed to fetch school exclusions'
  );
  const normalizedRequestedId = normalizeSchoolIdForComparison(schoolId);
  if (!normalizedRequestedId) return [];

  const result: string[] = [];

  summaries.forEach(summary => {
    const hasSchool = summary.schoolsExcluded.some(school =>
      normalizeSchoolIdForComparison(school.schoolId) === normalizedRequestedId
    );
    if (hasSchool) {
      result.push(String(summary.bookingSystemId));
    }
  });

  return result;
}

export async function updateBookingSystemSchoolExclusions(
  schoolId: number | string,
  selectedIds: (number | string)[] | undefined,
  previousIds: (number | string)[] | undefined
): Promise<string[]> {
  const normalizeToCanonical = (ids: (number | string)[] | undefined): string[] =>
    (Array.isArray(ids) ? ids : [])
      .map(id => parseBookingSystemId(id))
      .filter((id): id is number => id !== null && id > 0)
      .map(id => String(id));

  const normalizedSelected = normalizeToCanonical(selectedIds);
  const normalizedPrevious = normalizeToCanonical(previousIds);

  const additions = normalizedSelected.filter(id => !normalizedPrevious.includes(id));
  const removals = normalizedPrevious.filter(id => !normalizedSelected.includes(id));

  const numericSchoolId = Number(schoolId);
  const effectiveSchoolId = Number.isNaN(numericSchoolId) ? schoolId : numericSchoolId;

  const bookingSystemIdsToCreate = additions
    .map(id => parseBookingSystemId(id))
    .filter((id): id is number => id !== null && id > 0);

  const bookingSystemIdsToDelete = removals
    .map(id => parseBookingSystemId(id))
    .filter((id): id is number => id !== null && id > 0);

  const numericEffectiveSchoolId = Number(effectiveSchoolId);
  const finalSchoolId = Number.isNaN(numericEffectiveSchoolId) ? 0 : numericEffectiveSchoolId;
  const localApi = await getLocalApi();
  const payload = {
    bookingSystemIdsToCreate,
    bookingSystemIdsToDelete,
    schoolId: finalSchoolId
  };

  const response = await localApi.call(
    apiConfig.booking_system_exclusion_update_school_bulk,
    'PUT',
    JSON.stringify(payload)
  );

  if (!response?.ok) {
    const errorMessage = await parseMessage(response);
    throw new Error(errorMessage ?? 'Failed to update booking system exclusions for school.');
  }

  return normalizedSelected;
}
